// frontend/packages/business-console/src/services/mediaService.js

import { api } from '@upryzze/services';

const ensure = (v, name) => {
  const s = String(v || '').trim();
  if (!s) throw new Error(`${name} is required.`);
  return s;
};

const toInt = (v, fallback) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export async function listMediaAssets({
  type,
  search,
  page = 1,
  pageSize = 48,
} = {}) {
  const params = {};
  if (type) params.type = type;
  if (search) params.search = search;
  params.page = toInt(page, 1);
  params.pageSize = toInt(pageSize, 48);

  const { data } = await api.get('/v2/media-assets', { params });

  if (Array.isArray(data)) {
    return { items: data, total: data.length, page: 1, pageSize: data.length };
  }

  const items = Array.isArray(data?.items) ? data.items : [];
  return {
    items,
    total: toInt(data?.total, items.length),
    page: toInt(data?.page, 1),
    pageSize: toInt(data?.pageSize, items.length),
  };
}

// Every asset matching type/search, across all pages. The list endpoint pages
// (48 by default) and the library + picker never asked for page 2, so a
// tenant with more than one page of media silently lost the rest from view
// (and the library's counts were page-1 counts). Folder and tag filtering is
// client-side, so callers need the full set anyway. maxItems is a safety cap.
export async function listAllMediaAssets({
  type,
  search,
  pageSize = 100,
  maxItems = 2000,
} = {}) {
  const all = [];
  let total = 0;
  for (let page = 1; all.length < maxItems; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const result = await listMediaAssets({ type, search, page, pageSize });
    const items = result?.items || [];
    total = result?.total ?? total;
    all.push(...items);
    if (items.length < pageSize || all.length >= total) break;
  }
  return { items: all.slice(0, maxItems), total: Math.max(total, all.length) };
}

export async function createCloudinaryUploadIntent({
  resourceType,
  filename,
  bytes,
  folder,
  tags,
} = {}) {
  const rt =
    String(resourceType || 'image').toLowerCase() === 'video'
      ? 'video'
      : 'image';
  const fn = ensure(filename || 'upload', 'filename');
  const b = toInt(bytes, 0);

  const body = {
    resourceType: rt,
    filename: fn,
    folder: folder || '',
    tags: Array.isArray(tags) ? tags : [],
  };

  if (b) body.bytes = b;

  const { data } = await api.post('/v2/media/cloudinary/upload-intent', body);
  return data || null;
}

export async function getCloudinaryUploadSignature(paramsToSign) {
  if (!paramsToSign || typeof paramsToSign !== 'object') {
    throw new Error('paramsToSign is required.');
  }
  const { data } = await api.post('/v2/media/cloudinary/signature', {
    paramsToSign,
  });
  return data || null;
}

export async function createMediaAssetFromCloudinary(resultInfo) {
  if (!resultInfo || typeof resultInfo !== 'object') {
    throw new Error('Cloudinary result info is required.');
  }

  const body = {
    public_id: resultInfo.public_id || resultInfo.publicId,
    secure_url: resultInfo.secure_url || resultInfo.secureUrl || resultInfo.url,
    resource_type: resultInfo.resource_type || resultInfo.resourceType,
    format: resultInfo.format,
    bytes: resultInfo.bytes,
    width: resultInfo.width,
    height: resultInfo.height,
    duration: resultInfo.duration,
    folder: resultInfo.folder,
    tags: Array.isArray(resultInfo.tags) ? resultInfo.tags : [],
    original_filename:
      resultInfo.original_filename || resultInfo.originalFilename,
  };

  const { data } = await api.post('/v2/media-assets/cloudinary', body);
  return data || null;
}

export async function updateMediaAsset(assetId, payload = {}) {
  const id = ensure(assetId, 'assetId');
  const { data } = await api.patch(
    `/v2/media-assets/${encodeURIComponent(id)}`,
    payload
  );
  return data || null;
}

export async function deleteMediaAsset(assetId) {
  const id = ensure(assetId, 'assetId');
  const { data } = await api.delete(
    `/v2/media-assets/${encodeURIComponent(id)}`
  );
  return data || null;
}

// On-demand usage backref: which content files reference this asset. Computed
// live by the content route (no stored index), so it may take a moment on
// large sites. Returns { count, files: [{ path, name, collection }], scanned,
// truncated }.
export async function getMediaUsage(assetId) {
  const id = ensure(assetId, 'assetId');
  const { data } = await api.get('/v2/content/media-usage', {
    params: { assetId: id },
  });
  return data || { count: 0, files: [] };
}

export async function listRecentlyUsed(limit = 10) {
  const { data } = await api.get('/v2/media/recently-used', {
    params: { limit },
  });
  const items = Array.isArray(data?.items)
    ? data.items
    : Array.isArray(data)
      ? data
      : [];
  return items;
}

export async function markRecentlyUsed(providerPublicId) {
  const pid = ensure(providerPublicId, 'providerPublicId');
  const { data } = await api.post('/v2/media/recently-used', {
    providerPublicId: pid,
  });
  return data || null;
}

// Website document (PDF / Word / Excel / PowerPoint) for a site's document
// archive. Server-side validated upload into the media library (Cloudinary
// refuses PDFs). Returns the serialized asset; use its `publicUrl`.
export async function uploadDocument(file) {
  if (!file) throw new Error('file is required.');
  const form = new FormData();
  form.append('file', file);
  // No Content-Type header: the browser sets multipart with its boundary.
  const { data } = await api.post('/v2/media-assets/document', form);
  return data || null;
}
